import { FlexyIconButton } from '@europrocurement/flexy-components';
import { addNoteIcon, addTaskIcon, sendMails } from '@europrocurement/l2d-icons';
import { Box, useTheme } from '@mui/material';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { type DossierOpenSearch } from '@europrocurement/l2d-domain';
import { useModalMail } from '@europrocurement/l2d-modules/modules/MailForm';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { type MailContactType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import { Tiers } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { useSnackbar } from 'notistack';
import DetailDossierActionMenu from '../DetailDossierActionMenu/DetailDossierActionMenu';
import useDossierActionMenuItemsBack from '../../../hooks/useDossierActionMenuItemsBack';
import DossierActionContext from '../../../providers/DossierActionContext';
import DossierRoutingContext from '../../../providers/DossierRoutingContext';
import useAdditionnalCollectionRequest from '../../../hooks/useAdditionnalCollectionRequest';
import {
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtxDispatch,
} from '../../../providers/CurrentDossierProvider';
import useActivitiesHook from '../../../hooks/useActivitiesHook';

type DossierCustomHeaderHeaderActionsBlocProps = {
    dossier: DossierOpenSearch;
    siblings: DossierOpenSearch[];
    prescripteur: Tiers;
};
const DossierCustomHeaderHeaderActionsBloc: React.FunctionComponent<DossierCustomHeaderHeaderActionsBlocProps> =
    function ({ dossier, siblings, prescripteur }) {
        const [anchorElHeader, setAnchorElHeader] = useState<null | HTMLElement>(null);

        const { palette } = useTheme();
        const { triggerAction } = useContext(DossierActionContext);
        const { getOrderDuplicateRoute } = useContext(DossierRoutingContext);
        const { callAdditionnalCollectionRequestModal } = useAdditionnalCollectionRequest();
        const { showMailModal } = useModalMail();
        const dispatch = useCurrentDossierCtxDispatch();
        const keycloakService = UseKeycloakService();
        const { enqueueSnackbar } = useSnackbar();

        const refreshDossier = useCallback(() => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
            }
        }, [dispatch]);

        const { handleAddTask } = useTasksHook({
            apiTarget: 'formalite',
            linkedEntityId: dossier?.id?.toString(),
        });

        const onNoteSuccess = useCallback(() => {
            enqueueSnackbar('La note a bien été ajoutée');
        }, [enqueueSnackbar]);

        const { handleAddNote } = useActivitiesHook({
            actionOnSuccess: onNoteSuccess,
        });

        const { dossierDetatilQuickActionBack } = useDossierActionMenuItemsBack({
            dossier,
            triggerAction,
            setAnchorEl: setAnchorElHeader,
            refreshDossier,
            getOrderDuplicateRoute,
            callAdditionnalCollectionRequestModal,
            siblings,
        });

        const contactEmailAttitreOuPrincipal = useMemo(() => {
            if (dossier?.contactId && prescripteur && prescripteur.contacts) {
                let contact = prescripteur.contacts.find((ctct) => ctct.id === dossier.contactId);

                if (!contact || !contact.email) {
                    contact = prescripteur.contacts.find((ctct) => ctct.main === true);
                }

                if (contact && contact.email) {
                    return {
                        emailAddress: contact.email,
                        firstName: contact.firstName,
                        lastName: contact.lastName,
                    } as MailContactType;
                }
            }
            return undefined;
        }, [dossier?.contactId, prescripteur]);

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Send mail */}
                <FlexyIconButton
                    displayTooltip
                    icon={sendMails}
                    onClick={() => {
                        let mailBodyContent = `Bonjour ${contactEmailAttitreOuPrincipal?.firstName}  ${contactEmailAttitreOuPrincipal?.lastName}\n\n\n\n`;
                        mailBodyContent += `Cordialement,\n${keycloakService.getName()}\n${keycloakService.getEmail()}`;
                        showMailModal({
                            to: contactEmailAttitreOuPrincipal
                                ? [contactEmailAttitreOuPrincipal]
                                : [],
                            subject: `Legal2digital - ${dossier.collectionNumero} - #${dossier.numero}`,
                            showTo: true,
                            searchParamsTiersIds: [dossier.prescripteurId as number],
                            body: mailBodyContent,
                            dossiersIds: dossier?.id ? [dossier.id] : [],
                            collectionIds: dossier?.collectionId ? [dossier.collectionId] : [],
                            showDriveDocButton: true,
                        });
                    }}
                    tooltip="Envoyer un e-mail"
                    iconOverwriteProps={{
                        color: palette.primary.dark,
                    }}
                />

                {/* Add Tâche */}
                <FlexyIconButton
                    displayTooltip
                    icon={addTaskIcon}
                    onClick={() => handleAddTask()}
                    tooltip="Ajouter une tâche"
                    iconOverwriteProps={{
                        color: palette.primary.dark,
                    }}
                />

                {/* Add Note */}
                <FlexyIconButton
                    displayTooltip
                    icon={addNoteIcon}
                    onClick={() => handleAddNote()}
                    tooltip="Ajouter une note"
                    iconOverwriteProps={{
                        color: palette.primary.dark,
                    }}
                />

                <DetailDossierActionMenu
                    actions={dossierDetatilQuickActionBack}
                    anchorEl={anchorElHeader}
                    setAnchorEl={setAnchorElHeader}
                    verticalDivider={false}
                />
            </Box>
        );
    };

export default DossierCustomHeaderHeaderActionsBloc;
